// Hex Math Utilities - Shared geometry calculations for flat-top hexagons
// All hex positioning logic should use this module to ensure consistency

/**
 * Hex geometry for flat-top orientation:
 * - Width = 2 * circumradius (R)
 * - Height = sqrt(3) * R
 * - Horizontal spacing = 1.5 * R (3/4 of width)
 * - Odd columns offset down by height/2
 */

export const HexMath = {
    /**
     * Convert hex grid coordinates to pixel position (center of hex)
     *
     * @param {number} col - Hex column
     * @param {number} row - Hex row
     * @param {number} hexSize - Circumradius of hex
     * @param {number} offsetX - X offset for canvas positioning
     * @param {number} offsetY - Y offset for canvas positioning
     * @returns {{x: number, y: number}} Pixel position
     */
    hexToPixel(col, row, hexSize, offsetX = 0, offsetY = 0) {
        const width = hexSize * 2;
        const height = hexSize * Math.sqrt(3);

        // Horizontal: each column is 3/4 width apart
        const x = col * width * 0.75 + offsetX;

        // Vertical: odd columns offset down by half height
        const y = row * height + (col % 2 !== 0 ? height / 2 : 0) + offsetY;

        return { x, y };
    },

    /**
     * Convert pixel position to hex coordinates
     *
     * @param {number} px - Pixel X
     * @param {number} py - Pixel Y
     * @param {number} hexSize - Circumradius of hex
     * @param {number} offsetX - X offset used in hexToPixel
     * @param {number} offsetY - Y offset used in hexToPixel
     * @returns {{col: number, row: number}} Hex coordinates
     */
    pixelToHex(px, py, hexSize, offsetX = 0, offsetY = 0) {
        const width = hexSize * 2;
        const height = hexSize * Math.sqrt(3);

        // Remove offset
        const adjustedX = px - offsetX;
        const adjustedY = py - offsetY;

        // Estimate column
        const col = Math.round(adjustedX / (width * 0.75));

        // Account for odd column offset
        const isOddCol = col % 2 !== 0;
        const yOffset = isOddCol ? height / 2 : 0;
        const row = Math.round((adjustedY - yOffset) / height);

        return { col, row };
    },

    /**
     * Get the 6 corner positions of a flat-top hex
     *
     * In canvas coordinates (Y increases downward):
     * - 0° = right, 90° = down, 180° = left, 270° = up
     * - For flat-top: corners at 0°, 60°, 120°, 180°, 240°, 300°
     * - This puts flat edges at top and bottom, points at left and right
     *
     * @param {number} centerX - Center X position
     * @param {number} centerY - Center Y position
     * @param {number} hexSize - Circumradius of hex
     * @returns {Array<{x: number, y: number}>} Array of 6 corner positions
     */
    getHexCorners(centerX, centerY, hexSize) {
        const corners = [];
        for (let i = 0; i < 6; i++) {
            // Flat-top hex in canvas coords: start at 0 degrees
            // Corners at: 0, 60, 120, 180, 240, 300 degrees
            const angleDeg = 60 * i;
            const angleRad = (Math.PI / 180) * angleDeg;
            corners.push({
                x: centerX + hexSize * Math.cos(angleRad),
                y: centerY + hexSize * Math.sin(angleRad)
            });
        }
        return corners;
    },

    /**
     * Calculate canvas dimensions needed to render a hex map
     *
     * @param {Object} bounds - Map bounds {min: {x, y}, max: {x, y}}
     * @param {number} hexSize - Circumradius of hex
     * @param {number} padding - Extra padding around the map
     * @returns {{width: number, height: number, offsetX: number, offsetY: number, hexSize: number}}
     */
    calculateCanvasSize(bounds, hexSize, padding = 0) {
        const height = hexSize * Math.sqrt(3);
        const width = hexSize * 2;

        // Find actual min/max pixel positions by checking all corner positions
        // Need to check both min and max coordinates, accounting for odd column offsets
        let minPixelX = Infinity;
        let minPixelY = Infinity;
        let maxPixelX = -Infinity;
        let maxPixelY = -Infinity;

        // Check all four corners of the bounds rectangle
        for (let x = bounds.min.x; x <= bounds.max.x; x++) {
            for (let y = bounds.min.y; y <= bounds.max.y; y++) {
                const pos = this.hexToPixel(x, y, hexSize);
                minPixelX = Math.min(minPixelX, pos.x);
                minPixelY = Math.min(minPixelY, pos.y);
                maxPixelX = Math.max(maxPixelX, pos.x);
                maxPixelY = Math.max(maxPixelY, pos.y);
            }
        }

        // Add hex extents: hexSize on left/right, height/2 on top/bottom
        const canvasWidth = (maxPixelX - minPixelX) + width + padding * 2;
        const canvasHeight = (maxPixelY - minPixelY) + height + padding * 2;

        // Offset to position grid at origin + padding
        const offsetX = -minPixelX + padding + hexSize;
        const offsetY = -minPixelY + padding + height / 2;

        return {
            width: Math.ceil(canvasWidth),
            height: Math.ceil(canvasHeight),
            offsetX,
            offsetY,
            hexSize
        };
    },

    /**
     * Calculate hexSize from tile dimensions
     * For flat-top: width = 2 * circumradius
     *
     * @param {number} tileWidth - Tile image width in pixels
     * @returns {number} Hex circumradius
     */
    hexSizeFromTileWidth(tileWidth) {
        return tileWidth / 2;
    },

    /**
     * Calculate display IDs for all hexes in a map
     * Display IDs are 4-digit numbers: XXYY where XX is 1-indexed column, YY is 1-indexed row
     * The leftmost column is 01, the topmost row is 01
     *
     * @param {Array} tiles - Array of tile objects with pos.x and pos.y
     * @returns {{displayIds: Object, minCol: number, minRow: number}} Map of sourceId -> displayId
     */
    calculateDisplayIds(tiles) {
        if (!tiles || tiles.length === 0) {
            return { displayIds: {}, minCol: 0, minRow: 0 };
        }

        // Find min column and row (the offset for 1-indexing)
        let minCol = Infinity;
        let minRow = Infinity;

        for (const tile of tiles) {
            minCol = Math.min(minCol, tile.pos.x);
            minRow = Math.min(minRow, tile.pos.y);
        }

        // Build display ID mapping
        const displayIds = {};

        for (const tile of tiles) {
            const sourceId = `${tile.pos.x},${tile.pos.y}`;
            const displayCol = tile.pos.x - minCol + 1;
            const displayRow = tile.pos.y - minRow + 1;
            const displayId = `${String(displayCol).padStart(2, '0')}${String(displayRow).padStart(2, '0')}`;
            displayIds[sourceId] = displayId;
        }

        return { displayIds, minCol, minRow };
    },

    /**
     * Get the width of a hex side (for font sizing)
     * For flat-top hex, side length equals the circumradius
     *
     * @param {number} hexSize - Circumradius of hex
     * @returns {number} Side length
     */
    getHexSideLength(hexSize) {
        return hexSize;
    },

    /**
     * Format hex coordinate as a valid CSS selector/Konva name
     * Converts "col,row" format to "hex_col_row" with negative signs as 'n'
     *
     * Examples:
     * - "3,5" -> "hex_3_5"
     * - "-6,5" -> "hex_n6_5"
     * - "0,-2" -> "hex_0_n2"
     *
     * @param {string} hexCoord - Hex coordinate in "col,row" format
     * @returns {string} CSS-safe selector name
     */
    formatHexNameForSelector(hexCoord) {
        return 'hex_' + hexCoord.replace(/-/g, 'n').replace(',', '_');
    },

    /**
     * Calculate the actual bounds from an array of tiles
     * More accurate than trusting bounds in JSON data
     *
     * @param {Array} tiles - Array of tile objects with pos.x and pos.y
     * @returns {{min: {x: number, y: number}, max: {x: number, y: number}}} Bounds object
     */
    calculateActualBounds(tiles) {
        if (!tiles || tiles.length === 0) {
            return { min: { x: 0, y: 0 }, max: { x: 0, y: 0 } };
        }

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        for (const tile of tiles) {
            minX = Math.min(minX, tile.pos.x);
            minY = Math.min(minY, tile.pos.y);
            maxX = Math.max(maxX, tile.pos.x);
            maxY = Math.max(maxY, tile.pos.y);
        }

        return {
            min: { x: minX, y: minY },
            max: { x: maxX, y: maxY }
        };
    },

    /**
     * Get neighbor hex coordinates in a given direction (4-direction navigation)
     * For flat-top hexes with offset coordinates, this provides intuitive arrow key navigation
     *
     * @param {number} col - Current hex column
     * @param {number} row - Current hex row
     * @param {string} direction - Direction: 'up', 'down', 'left', 'right'
     * @returns {{col: number, row: number}} Neighbor hex coordinates
     */
    getNeighbor(col, row, direction) {
        // For flat-top hexes with odd-column offset, 4-direction navigation is straightforward:
        // - Left/Right: move between columns (col ± 1)
        // - Up/Down: move within column (row ± 1)
        // This provides intuitive navigation that matches user expectations

        switch (direction.toLowerCase()) {
            case 'up':
                return { col, row: row - 1 };
            case 'down':
                return { col, row: row + 1 };
            case 'left':
                return { col: col - 1, row };
            case 'right':
                return { col: col + 1, row };
            default:
                throw new Error(`Invalid direction: ${direction}. Must be 'up', 'down', 'left', or 'right'`);
        }
    }
};

export default HexMath;
